// Fonctions pures de l'écran Carte (PIL-01 lot 4). Aucune dépendance à leaflet / react : testables en node.
import {CIBLE_REMPLISSAGE, VERDICT_LIBELLES} from '../../tokens.js';

export const CELLULE_CLUSTER_PX = 60;
export const COTE_GRILLE_M = 500;

/**
 * Côté (px) du marqueur abribac par palier de zoom : 28 jusqu'au zoom 13, 32 aux zooms 14-15, 40 au zoom 16,
 * 48 à partir de 17 ; sélectionné = palier + 8 (le halo orange reste autour).
 */
export const SELECTION_ABRIBAC_PX = 8;
export function tailleAbribac(zoom, selectionne) {
    const z = Math.floor(zoom);
    const palier = z <= 13 ? 28 : z <= 15 ? 32 : z === 16 ? 40 : 48;
    return selectionne ? palier + SELECTION_ABRIBAC_PX : palier;
}

/** Liseré blanc extérieur du corps, en px d'écran selon le côté du marqueur : 1,5 à 28-32, 2 à 40, 2,5 à 48. */
export function lisereAbribac(taille) {
    return taille < 40 ? 1.5 : taille < 48 ? 2 : 2.5;
}

/** Contour coloré du corps, en px d'écran, sur le même barème : 1 à 28-32, 1,25 à 40, 1,5 à 48. */
export function contourAbribac(taille) {
    return taille < 40 ? 1 : taille < 48 ? 1.25 : 1.5;
}
const M_PAR_DEGRE_LAT = 111320;

/**
 * R6 : regroupement par grille écran. `projeter(point, zoom)` -> {x, y} en pixels écran au zoom donné.
 * Un point appartient à la cellule floor(x / 60), floor(y / 60). Renvoie un cluster par cellule non vide
 * (`nb === 1` = point isolé, à dessiner comme un marqueur), avec le barycentre en pixels et en lat/lng.
 */
export function clusteriser(points, zoom, projeter, cellule = CELLULE_CLUSTER_PX) {
    const cellules = new Map();
    for (const p of points) {
        const {x, y} = projeter(p, zoom);
        const cle = `${Math.floor(x / cellule)}:${Math.floor(y / cellule)}`;
        let c = cellules.get(cle);
        if (!c) {
            c = {cle, nb: 0, ids: [], sx: 0, sy: 0, slat: 0, slng: 0};
            cellules.set(cle, c);
        }
        c.nb++;
        c.ids.push(p.id);
        c.sx += x;
        c.sy += y;
        c.slat += p.lat;
        c.slng += p.lng;
    }
    return [...cellules.values()].map((c) => ({
        cle: c.cle,
        nb: c.nb,
        ids: c.ids,
        x: c.sx / c.nb,
        y: c.sy / c.nb,
        lat: c.slat / c.nb,
        lng: c.slng / c.nb,
    }));
}

/**
 * R7 : grille de carrés de 500 m. Les cellules sont alignées sur le repère (lat, lng) = (0, 0) et la largeur en
 * degrés de longitude est fixée à la latitude moyenne des PAV (cellules identiques d'un rendu à l'autre).
 * Métrique selon l'acte des `etats` reçus (ceux de l'acte courant) :
 *  - `lavage` / `maint` / `graffiti` : `part` = (PAV `pb` + `imp`) / PAV inspectés (`ras` + `pb` + `imp`), `null` si aucun inspecté ;
 *  - `collecte` : `part` = taux de remplissage moyen des PAV de la cellule (moyenne des `etat.tauxRemplissage`), `null` sans collecte.
 * Les PAV sans coordonnées sont ignorés.
 * @returns {{cle, nb, enDefaut, inspectes, part, sud, ouest, nord, est}[]}
 */
export function cellulesGrille(pavs, etats, cote = COTE_GRILLE_M, acte = null) {
    const remplissage = acte === 'collecte';
    const localises = pavs.filter((p) => p.lat != null && p.lng != null);
    if (localises.length === 0) return [];
    const latMoyenne = localises.reduce((s, p) => s + p.lat, 0) / localises.length;
    const dLat = cote / M_PAR_DEGRE_LAT;
    const dLng = cote / (M_PAR_DEGRE_LAT * Math.cos((latMoyenne * Math.PI) / 180));
    const cellules = new Map();
    for (const p of localises) {
        const i = Math.floor(p.lat / dLat);
        const j = Math.floor(p.lng / dLng);
        const cle = `${i}:${j}`;
        let c = cellules.get(cle);
        if (!c) {
            c = {cle, nb: 0, enDefaut: 0, inspectes: 0, sommeTaux: 0, nbTaux: 0, sud: i * dLat, ouest: j * dLng, nord: (i + 1) * dLat, est: (j + 1) * dLng};
            cellules.set(cle, c);
        }
        c.nb++;
        const etat = etats.get(p.id);
        if (etat.verdict === 'pb' || etat.verdict === 'imp') c.enDefaut++;
        if (etat.verdict !== 'ni') c.inspectes++;
        if (typeof etat.tauxRemplissage === 'number') { c.sommeTaux += etat.tauxRemplissage; c.nbTaux++; }
    }
    return [...cellules.values()].map(({sommeTaux, nbTaux, ...c}) => ({
        ...c,
        part: remplissage ? (nbTaux > 0 ? sommeTaux / nbTaux : null) : (c.inspectes > 0 ? c.enDefaut / c.inspectes : null),
    }));
}

/** R7 : part de PAV en défaut par commune (clé de commune). Map<cle, {nb, enDefaut, part}>. */
export function partsParCommune(pavs, etats) {
    const res = new Map();
    for (const p of pavs) {
        if (p.communeCle == null) continue;
        let c = res.get(p.communeCle);
        if (!c) {
            c = {nb: 0, enDefaut: 0, part: 0};
            res.set(p.communeCle, c);
        }
        c.nb++;
        if (etats.get(p.id).verdict === 'pb') c.enDefaut++;
    }
    for (const c of res.values()) c.part = c.enDefaut / c.nb;
    return res;
}

/** R7 : poids de la heatmap = nombre d'éléments en défaut (au moins 1 pour un PAV en problème), 0 sinon. */
export function poidsDefaut(etat) {
    if (etat.verdict !== 'pb') return 0;
    return Math.max(1, etat.elementsEnDefaut.length);
}

/** Tonnage lisible : kg sous 10 000 kg, sinon tonnes à 1 décimale (« 2 153,8 t »). */
export function formatTonnage(kg) {
    if (kg == null) return '—';
    return kg < 10000 ? `${Math.round(kg).toLocaleString('fr-FR')} kg` : `${(kg / 1000).toLocaleString('fr-FR', {minimumFractionDigits: 1, maximumFractionDigits: 1})} t`;
}

// Échelle du remplissage (grille et heatmap, acte collecte) : rampe divergente centrée sur la cible (CIBLE_REMPLISSAGE).
// Paliers dérivés de la cible (± 0,09 / ± 0,18) : bleu, bleu clair, jaune (cible), orange, rouge ; interpolation linéaire, clamp aux extrémités.
const decaler = (ecart) => Math.round((CIBLE_REMPLISSAGE + ecart) * 100) / 100;
export const PALIERS_REMPLISSAGE = [
    [decaler(-0.18), [24, 95, 165]], // #185FA5
    [decaler(-0.09), [133, 183, 235]], // #85B7EB
    [CIBLE_REMPLISSAGE, [250, 199, 117]], // #FAC775
    [decaler(0.09), [239, 159, 39]], // #EF9F27
    [decaler(0.18), [178, 34, 34]], // #B22222
];
export const REMPLISSAGE_MIN = PALIERS_REMPLISSAGE[0][0];
export const REMPLISSAGE_MAX = PALIERS_REMPLISSAGE[PALIERS_REMPLISSAGE.length - 1][0];

/** [r, g, b] d'un taux de remplissage (interpolation linéaire entre paliers, borné au premier et au dernier palier). */
export function rgbRemplissage(taux) {
    const P = PALIERS_REMPLISSAGE;
    if (!(taux > P[0][0])) return P[0][1];
    for (let i = 1; i < P.length; i++) {
        const [t1, c1] = P[i];
        if (taux <= t1) {
            const [t0, c0] = P[i - 1];
            const k = (taux - t0) / (t1 - t0);
            return c0.map((x, n) => Math.round(x + (c1[n] - x) * k));
        }
    }
    return P[P.length - 1][1];
}

export function couleurRemplissage(taux) {
    const [r, g, b] = rgbRemplissage(taux);
    return `rgb(${r}, ${g}, ${b})`;
}

const PALETTE_PART = [
    [0, [135, 188, 38]], // #87BC26 (RAS)
    [0.5, [243, 154, 63]], // #F39A3F
    [1, [178, 34, 34]], // #B22222
];

/** Couleur d'une part (0..1) : vert RAS -> orange -> rouge. */
export function couleurPart(part) {
    const v = Math.min(1, Math.max(0, part));
    for (let i = 1; i < PALETTE_PART.length; i++) {
        const [t1, c1] = PALETTE_PART[i];
        if (v <= t1) {
            const [t0, c0] = PALETTE_PART[i - 1];
            const k = (v - t0) / (t1 - t0);
            const rgb = c0.map((x, n) => Math.round(x + (c1[n] - x) * k));
            return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
        }
    }
    return 'rgb(178, 34, 34)';
}

// --- Curseur temporel (R8) -------------------------------------------------------------------------------------

const MS_JOUR = 86400000;

function versUtc(jour) {
    return Date.parse(`${jour}T00:00:00Z`);
}

/** Nombre de jours entre deux dates Y-m-d (b - a). */
export function joursEntre(a, b) {
    return Math.round((versUtc(b) - versUtc(a)) / MS_JOUR);
}

/** Date Y-m-d décalée de `n` jours (calendrier UTC : pas de dérive d'heure d'été). */
export function ajouterJours(jour, n) {
    return new Date(versUtc(jour) + n * MS_JOUR).toISOString().slice(0, 10);
}

/**
 * Bornes du curseur : de la première donnée (`index.bornes.min`) à aujourd'hui. `max` est toujours aujourd'hui
 * (jamais une date future, même si une donnée l'est) ; `min` est plafonné à `max` ; sans donnée, `min = max`.
 */
export function bornesCurseur(index, aujourdhui) {
    const premiere = index && index.bornes ? index.bornes.min : null;
    const min = premiere && premiere < aujourdhui ? premiere : aujourdhui;
    return {min, max: aujourdhui, jours: joursEntre(min, aujourdhui)};
}

/** Ramène `at` dans [min, max]. */
export function bornerDate(at, {min, max}) {
    if (!at || at > max) return max;
    return at < min ? min : at;
}

/** « 2025-07-29 » -> « 29/07/2025 » ; vide -> « — ». */
export function dateFr(jour) {
    if (!jour) return '—';
    const [a, m, j] = jour.split('-');
    return `${j}/${m}/${a}`;
}

/** Années de la plage (repères sous le curseur). */
export function anneesCurseur({min, max}) {
    const debut = Number(min.slice(0, 4));
    const fin = Number(max.slice(0, 4));
    const res = [];
    for (let a = debut; a <= fin; a++) res.push(a);
    return res;
}

// --- Libellés du panneau et de la popup ---------------------------------------------------------------------


/** « PROBLÈME · pédale » : verdict en majuscules, éléments en défaut en minuscules. Collecte : « COLLECTÉ » / « COLLECTE IMPOSSIBLE ». */
export function libelleEtat(etat) {
    if (etat.evenement && etat.evenement.type === 'COLLECTE') return 'COLLECTÉ';
    if (etat.evenement && etat.evenement.type === 'COLLECTE_IMPOSSIBLE') return 'COLLECTE IMPOSSIBLE';
    const base = VERDICT_LIBELLES[etat.verdict].toUpperCase();
    return etat.elementsEnDefaut.length > 0 ? `${base} · ${etat.elementsEnDefaut.map((e) => e.toLowerCase()).join(', ')}` : base;
}

const TYPES_PASSAGE = {LAVAGE: 'lavé', MAINT_PREV: 'maintenance', IMPOSSIBLE: 'impossible', GRAFFITI: 'graffiti', COLLECTE: 'collecte', COLLECTE_IMPOSSIBLE: 'collecte impossible'};

/** « 14/10/2026 · lavé » ; « — » sans passage. */
export function libelleDernierPassage(etat) {
    if (!etat.date) return '—';
    const type = etat.evenement ? TYPES_PASSAGE[etat.evenement.type] : null;
    return type ? `${dateFr(etat.date)} · ${type}` : dateFr(etat.date);
}

/** Dernier COLLECTE / COLLECTE_IMPOSSIBLE d'un PAV à la date `jusqua` incluse (événements triés date asc), sinon `null`. */
export function derniereCollecte(evenements, jusqua) {
    let res = null;
    for (const e of evenements || []) {
        if ((e.type === 'COLLECTE' || e.type === 'COLLECTE_IMPOSSIBLE') && (!jusqua || e.date <= jusqua)) res = e;
    }
    return res;
}

/** « 14/09/2026 · 74 kg » ; déclassé : « 23/09/2026 · bac déclassé » ; aucune collecte : « — ». */
export function libelleCollecte(evenement) {
    if (!evenement) return '—';
    if (evenement.type === 'COLLECTE_IMPOSSIBLE') return `${dateFr(evenement.date)} · bac déclassé`;
    const kg = evenement.detail && evenement.detail.tonnageKg;
    return kg == null ? dateFr(evenement.date) : `${dateFr(evenement.date)} · ${Math.round(kg).toLocaleString('fr-FR')} kg`;
}

/** Photos d'un événement (celles du passage + celles de chaque élément de checklist), dans l'ordre du snapshot. */
export function photosEvenement(evenement) {
    if (!evenement) return [];
    const res = [...(evenement.photos || [])];
    for (const el of evenement.elements || []) res.push(...(el.photos || []));
    return res;
}
